import {
  BarChart3,
  BookOpen,
  Clapperboard,
  Heart,
  Loader2,
  Shield,
} from "lucide-react";
import type { NaradaGroup, NaradaSchema, NaradaTheme } from "@/lib/api";
import { Button } from "@/components/ui/button";

const THEME_ICON: Record<string, typeof BookOpen> = {
  tresc: Clapperboard,
  oferta: BookOpen,
  liczby: BarChart3,
  pozakup: Heart,
  granice: Shield,
};

export default function ZespolAsk({
  question,
  onQuestion,
  themes,
  schemas,
  groups,
  themeId,
  onTheme,
  schemaId,
  onSchema,
  areaIds,
  onToggleArea,
  error,
  pending,
  disabled,
  onSubmit,
}: {
  question: string;
  onQuestion: (value: string) => void;
  themes: NaradaTheme[];
  schemas: NaradaSchema[];
  groups: NaradaGroup[];
  themeId: string;
  onTheme: (id: string) => void;
  schemaId: string;
  onSchema: (id: string) => void;
  areaIds: string[];
  onToggleArea: (id: string) => void;
  error: string;
  pending: boolean;
  disabled: boolean;
  onSubmit: () => void;
}) {
  const theme = themes.find((row) => row.id === themeId);
  const themeSchemas = schemas.filter((row) => theme?.schema_ids.includes(row.id));
  const selected = schemas.find((row) => row.id === schemaId);
  const tooShort = question.trim().length < 8;

  return (
    <section className="mx-auto max-w-3xl space-y-8">
      <div>
        <label htmlFor="narada-question" className="text-base font-medium text-slate-900">
          Co Cię nurtuje?
        </label>
        <p className="mt-1 text-sm text-slate-600">
          Napisz jak do koleżanki. Nie musisz znać nazw plików ani skrótów.
        </p>
        <textarea
          id="narada-question"
          value={question}
          onChange={(event) => onQuestion(event.target.value)}
          rows={5}
          disabled={disabled}
          placeholder="Np. boję się, że ten tydzień na Instagramie wygląda jak sam ból. Czy tak jest?"
          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base leading-relaxed text-slate-900 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/30 disabled:opacity-60"
        />
      </div>

      <div>
        <p className="text-base font-medium text-slate-900">O czym to jest?</p>
        <p className="mt-1 text-sm text-slate-600">
          Wybierz pokój. Zespół sam weźmie właściwe materiały.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {themes.map((row) => {
            const Icon = THEME_ICON[row.id] ?? BookOpen;
            const on = row.id === themeId;
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => onTheme(row.id)}
                disabled={disabled}
                className={`min-h-[5.5rem] rounded-xl border p-4 text-left shadow-sm transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 disabled:opacity-60 ${
                  on
                    ? "border-brand bg-white shadow-md ring-2 ring-brand/20"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow"
                }`}
              >
                <span className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${on ? "text-brand" : "text-slate-400"}`} />
                  <span className="text-sm font-medium text-slate-900">{row.label}</span>
                </span>
                <span className="mt-2 block text-sm leading-relaxed text-slate-600">
                  {row.plain}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <details className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <summary className="cursor-pointer text-sm font-medium text-slate-600 marker:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30">
          Dokładniej, dla Arka
        </summary>
        <div className="mt-4 space-y-5">
          <div>
            <p className="text-sm font-medium text-slate-900">Inny sposób myślenia</p>
            <p className="mt-1 text-sm text-slate-600">
              {selected?.plain || selected?.axis || "Zespół użyje domyślnego podejścia."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {themeSchemas.map((schema) => {
                const on = schema.id === schemaId;
                return (
                  <button
                    key={schema.id}
                    type="button"
                    onClick={() => onSchema(schema.id)}
                    disabled={disabled}
                    className={`min-h-11 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 ${
                      on
                        ? "border-brand bg-white text-slate-900 ring-2 ring-brand/20"
                        : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:text-slate-900"
                    }`}
                  >
                    {schema.label}
                    {schema.parking ? " (później)" : ""}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">
              Co zespół weźmie ze sobą ({areaIds.length})
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {groups.map((group) => (
                <fieldset key={group.id} className="space-y-2">
                  <legend className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {group.label}
                  </legend>
                  {group.areas.map((area) => (
                    <label key={area.id} className="flex min-h-9 items-start gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-[hsl(var(--brand))]"
                        checked={areaIds.includes(area.id)}
                        onChange={() => onToggleArea(area.id)}
                        disabled={disabled}
                      />
                      <span>{area.label}</span>
                    </label>
                  ))}
                </fieldset>
              ))}
            </div>
          </div>
        </div>
      </details>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <Button
        variant="brand"
        size="lg"
        className="min-h-12 w-full rounded-2xl sm:w-auto"
        disabled={disabled || pending || tooShort}
        onClick={onSubmit}
      >
        {pending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
            Wysyłam
          </>
        ) : (
          "Poproś zespół o zdanie"
        )}
      </Button>
      {tooShort && !disabled && (
        <p className="text-sm text-slate-500">Wpisz choć jedno pełne zdanie.</p>
      )}
    </section>
  );
}
